/**
 * Truncate the given string to the given length.
 *
 * @param {string} str
 * @param {number} length
 *
 * @returns string
 */
export const truncate = (str, length = 20) => {
  if (str?.length <= length) {
    return str;
  }

  return `${str?.substring(0, length)}...`;
};

/**
 *Interpolate the given string with delimiter ':' with the given parameters.
 *
 * @param {string} str
 * @param {object} params
 * @returns string
 * @example
 * interpolate('Hello :name', { name: 'John' }) // Hello John
 */
export const interpolate = (str, params = {}) => {
  const formattedString = Object.keys(params).reduce(
    (result, key) => result.replace(`:${key}`, params[key] ?? ""),
    str
  );

  return formattedString;
};

/**
 * Get full name
 * @param {string} name
 * @param {string} splitter
 * @param {Boolean} includeSplitter
 * @returns string
 */
export const getFullName = (name, splitter = ",", includeSplitter = true) => {
  const [lastName, firstName] = name?.split(splitter) || [];

  if (includeSplitter) {
    return `${lastName}, ${firstName}`;
  }

  return `${lastName} ${firstName}`;
};

/**
 * Get initial letter of full name
 * @param {string} name
 * @param {string} splitter
 * @returns string
 */
export const getInitials = (name, splitter = ",") => {
  const [lastName, firstName] = name?.split(splitter) || [];
  const firstNameInitial = firstName?.trim()?.[0] ?? "";
  const lastNameInitial = lastName?.trim()?.[0] ?? "";
  const initials = `${lastNameInitial}${firstNameInitial}`;
  return initials;
};

/**
 * Get capitalized first letter of string
 * @param {string} str
 * @returns string
 */
export const capitalizeFirstLetter = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Get capitalized first letter of each word in string
 * @param {string} str
 * @returns string
 */
export const capitalizeFirstLetterOfEachWord = (str) => {
  return str
    .split(" ")
    .map((word) => capitalizeFirstLetter(word))
    .join(" ");
};

/**
 * Convert numerical value to comma separated
 *
 * @param {number} amount
 * @returns string
 */
export const convertNumberToCommaSeparatedFormat = (
  amount = 0,
  options = {}
) => {
  const { minimumFractionDigits = 0, maximumFractionDigits = 2 } = options;

  const value = typeof amount === "string" ? parseFloat(amount) : amount;
  return value.toLocaleString("en-US", {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/**
 * Convert amount to currency
 *
 * @param {number} amount
 * @returns string
 */
export const convertToCurrency = (amount = 0, options = {}) => {
  const {
    currency = "USD",
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  } = options;

  const value = typeof amount === "string" ? parseFloat(amount) : amount;

  return value.toLocaleString("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/**
 * Retrieve search params from url
 *
 * @param {string} url
 * @returns object
 * @example
 * getSearchParams('https://example.com?name=John&age=30') // { name: 'John', age: '30' }
 */
export const getSearchParams = (url) => {
  const searchParams = new URLSearchParams(url);

  return Object.fromEntries(searchParams);
};

export const containsOnlyWhitespace = (inputString = "") => {
  if (
    !inputString ||
    typeof inputString !== "string" ||
    inputString.length === 0
  ) {
    return false;
  }

  return inputString.trim() === "";
};

export const checkIsInvalidValue = (inputString = "") => {
  return (
    inputString === null ||
    !inputString.toString().length ||
    containsOnlyWhitespace(inputString)
  );
};

export const checkIsAlphaNumeric = (inputString) => {
  const regex = /^[A-Za-z0-9]*$/;
  return regex.test(inputString);
};

export const addPrefixToString = (inputString, prefix = "#") => {
  return `${prefix}${inputString}`;
};

export const sortByFirstLetter = (words) => {
  return words.sort((a, b) =>
    (a[0]?.toLowerCase() || "").localeCompare(b[0]?.toLowerCase() || "")
  );
};

/**
 * Truncate input value to maximum length
 * @param {Event} event - Input event
 * @param {number} maxLength - Maximum allowed length
 */
export const truncateInput = (event, maxLength = 50) => {
  const input = event.target;
  if (input.value.length > maxLength) {
    input.value = input.value.slice(0, maxLength);
  }
};

/**
 * Separator to put before the item at `index` when listing `count` items as "A, B and C".
 * Use when each item is rendered separately (e.g. as JSX) instead of joined into one string.
 * Example: indexes 0, 1, 2 of 3 → "", ", ", " and "
 *
 * @param {number} index - Position of the item
 * @param {number} count - Total number of items
 * @returns {string} - Separator to render before the item
 */
export const getListSeparator = (index, count) => {
  if (index === 0) return "";
  return index === count - 1 ? " and " : ", ";
};

/**
 * Convert array to comma separated string with 'and' before the last item
 * Example: ["A", "B", "C"] → "A, B and C"
 *
 * @param {Array} array - Array of strings
 * @returns {string} - Comma separated string with 'and' before the last item
 */
export const convertArrayToCommaSeparatedString = (array = []) => {
  const items = array
    .filter(Boolean)
    .map((item) => item.toString().trim())
    .filter((item) => item.length > 0);

  return items
    .map((item, index) => `${getListSeparator(index, items.length)}${item}`)
    .join("");
};

/**
 * Prefix a word with the count, adding an "s" when the count is not 1
 * Example: pluralize(1, "day") → "1 day", pluralize(3, "day") → "3 days"
 *
 * @param {number} count - Number of items
 * @param {string} word - Singular form of the word
 * @returns {string} - Count followed by the singular or plural word
 */
export const pluralize = (count, word) => {
  return `${count} ${word}${count === 1 ? "" : "s"}`;
};
